import { getQuestionsBySection } from "./feedbackFlow";

import type {
  FeedbackFlow,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionAnswer,
  FeedbackFlowQuestionResponse,
  FeedbackFlowResponseMap,
} from "../types/experiment.types";

export type ParticipantQuestionStepId =
  | "personal_info"
  | "demographics"
  | "prototype";

type ParticipantBaseStep = {
  title: string;
  description: string;
};

export type ParticipantIntroStep = ParticipantBaseStep & {
  id: "intro";
  terms?: string | null;
};

type ParticipantQuestionStepBase<StepId extends ParticipantQuestionStepId> =
  ParticipantBaseStep & {
    id: StepId;
    questions: FeedbackFlowQuestion[];
  };

export type PersonalInfoParticipantStep =
  ParticipantQuestionStepBase<"personal_info">;

export type DemographicsParticipantStep =
  ParticipantQuestionStepBase<"demographics">;

export type PrototypeParticipantStep =
  ParticipantQuestionStepBase<"prototype">;

export type ParticipantQuestionStep =
  | PersonalInfoParticipantStep
  | DemographicsParticipantStep
  | PrototypeParticipantStep;

export type ParticipantOutroStep = ParticipantBaseStep & {
  id: "outro";
  terms?: string | null;
};

export type ParticipantFlowStep =
  | ParticipantIntroStep
  | ParticipantQuestionStep
  | ParticipantOutroStep;

export type ParticipantPreviewProgress = {
  completedUnits: number;
  completedQuestionCount: number;
  progressPct: number;
  totalQuestionCount: number;
  totalUnits: number;
};

export function normalizePreviewRoute(rawRoute: string): string {
  if (!rawRoute || !rawRoute.trim()) {
    return "/";
  }

  try {
    const parsed = /^https?:\/\//i.test(rawRoute)
      ? new URL(rawRoute)
      : new URL(rawRoute, window.location.origin);
    const pathname = parsed.pathname || "/";
    const withoutTrailingSlash = pathname.replace(/\/+$/, "");
    return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : "/";
  } catch {
    const withoutQuery = rawRoute.split("?")[0].split("#")[0] || "/";
    const normalized = withoutQuery.startsWith("/")
      ? withoutQuery
      : `/${withoutQuery}`;
    const withoutTrailingSlash = normalized.replace(/\/+$/, "");
    return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : "/";
  }
}

export function hasResponseValue(
  value: FeedbackFlowQuestionAnswer | undefined,
): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Boolean(value?.trim());
}

export function hasCompletedResponse(
  response: FeedbackFlowQuestionResponse | undefined,
): boolean {
  if (!hasResponseValue(response?.answer)) {
    return false;
  }

  return (
    response?.sentiment !== "dislike" ||
    Boolean(response.dislikeReason?.trim())
  );
}

function getActiveParticipantQuestions(
  feedbackFlow: FeedbackFlow,
): FeedbackFlowQuestion[] {
  return [
    ...(feedbackFlow.personalInfoEnabled
      ? getQuestionsBySection(feedbackFlow, "personal_info")
      : []),
    ...(feedbackFlow.demographicsEnabled
      ? getQuestionsBySection(feedbackFlow, "demographics")
      : []),
    ...getQuestionsBySection(feedbackFlow, "prototype"),
  ];
}

export function buildParticipantFlowSteps(
  feedbackFlow: FeedbackFlow,
): ParticipantFlowStep[] {
  const personalInfoQuestions = feedbackFlow.personalInfoEnabled
    ? getQuestionsBySection(feedbackFlow, "personal_info")
    : [];
  const demographicsQuestions = feedbackFlow.demographicsEnabled
    ? getQuestionsBySection(feedbackFlow, "demographics")
    : [];
  const prototypeQuestions = getQuestionsBySection(feedbackFlow, "prototype");
  const steps: ParticipantFlowStep[] = [];

  if (feedbackFlow.intro.enabled) {
    steps.push({
      id: "intro",
      title: "Welcome",
      description: feedbackFlow.intro.description,
      terms: feedbackFlow.intro.terms,
    });
  }

  if (personalInfoQuestions.length > 0) {
    steps.push({
      id: "personal_info",
      title: "Personal Information",
      description: "Share the contact or profile details requested for this study.",
      questions: personalInfoQuestions,
    });
  }

  if (demographicsQuestions.length > 0) {
    steps.push({
      id: "demographics",
      title: "Demographics",
      description:
        "Tell us a little about yourself before moving into the study.",
      questions: demographicsQuestions,
    });
  }

  if (prototypeQuestions.length > 0) {
    steps.push({
      id: "prototype",
      title: "Prototype Feedback",
      description: "Explore the product and answer the mapped questions.",
      questions: prototypeQuestions,
    });
  }

  if (feedbackFlow.outro.enabled) {
    steps.push({
      id: "outro",
      title: "Thank You",
      description: feedbackFlow.outro.description,
      terms: feedbackFlow.outro.terms,
    });
  }

  return steps;
}

export function isParticipantStepComplete(
  step: ParticipantFlowStep,
  responses: FeedbackFlowResponseMap,
  introCompleted: boolean,
  outroCompleted: boolean,
): boolean {
  if (step.id === "intro") {
    return introCompleted;
  }

  if (step.id === "outro") {
    return outroCompleted;
  }

  return step.questions.every((question) =>
    hasCompletedResponse(responses[question.id]),
  );
}

export function getParticipantPreviewProgress(params: {
  feedbackFlow: FeedbackFlow;
  responses: FeedbackFlowResponseMap;
  introCompleted: boolean;
  outroCompleted: boolean;
}): ParticipantPreviewProgress {
  const { feedbackFlow, responses, introCompleted, outroCompleted } = params;
  const activeQuestions = getActiveParticipantQuestions(feedbackFlow);
  const totalQuestionCount = activeQuestions.length;
  const completedQuestionCount = activeQuestions.filter((question) =>
    hasCompletedResponse(responses[question.id]),
  ).length;
  const totalUnits =
    totalQuestionCount +
    (feedbackFlow.intro.enabled ? 1 : 0) +
    (feedbackFlow.outro.enabled ? 1 : 0);
  const completedUnits =
    completedQuestionCount +
    (feedbackFlow.intro.enabled && introCompleted ? 1 : 0) +
    (feedbackFlow.outro.enabled && outroCompleted ? 1 : 0);

  return {
    completedUnits,
    completedQuestionCount,
    progressPct: totalUnits > 0 ? (completedUnits / totalUnits) * 100 : 0,
    totalQuestionCount,
    totalUnits,
  };
}

export function getPrototypeRoutes(questions: FeedbackFlowQuestion[]): string[] {
  const routes = new Set<string>();

  for (const question of questions) {
    if (!question.taggedTarget?.route) {
      continue;
    }

    routes.add(normalizePreviewRoute(question.taggedTarget.route));
  }

  return Array.from(routes);
}
